import type { PostHogConfig } from 'posthog-js';

import type { User } from '@/api/auth';

const SERVICE_NAME = 'erp24-frontend';
const MAX_STRING_LENGTH = 160;
const TOKEN_LIKE_RE = /\b[A-Za-z0-9._~+/=-]{24,}\b/g;

type ProductAnalyticsPrimitive = string | number | boolean;
export type ProductAnalyticsProperties = Record<
  string,
  ProductAnalyticsPrimitive | null | undefined
>;

type ProductAnalyticsAdapter = {
  capture: (event: string, properties?: Record<string, unknown>) => void;
  identify: (distinctId: string, userProperties?: Record<string, unknown>) => void;
  reset: (resetDeviceId?: boolean) => void;
  debug?: (enabled?: boolean) => void;
};

type PartialPostHogConfig = Partial<PostHogConfig>;

interface ProductAnalyticsEnv {
  apiHost: string;
  apiKey: string;
  debugEnabled: boolean;
  enabled: boolean;
}

let activeAdapter: ProductAnalyticsAdapter | null = null;
let initPromise: Promise<ProductAnalyticsAdapter | null> | null = null;
let initStarted = false;
let testAdapter: ProductAnalyticsAdapter | null = null;

function trimEnv(value: string | undefined): string {
  return value?.trim() ?? '';
}

function currentEnv(): ProductAnalyticsEnv {
  const apiKey = trimEnv(import.meta.env.VITE_POSTHOG_KEY);
  const apiHost = trimEnv(import.meta.env.VITE_POSTHOG_HOST);
  const debugEnabled = trimEnv(import.meta.env.VITE_POSTHOG_DEBUG) === '1';
  return {
    apiHost,
    apiKey,
    debugEnabled,
    enabled: Boolean(apiKey && apiHost),
  };
}

function envName(): string {
  return trimEnv(import.meta.env.VITE_ENVIRONMENT) || trimEnv(import.meta.env.MODE) || 'unknown';
}

function deploySha(): string {
  return trimEnv(import.meta.env.VITE_DEPLOY_SHA) || 'unknown';
}

function releaseId(): string {
  return trimEnv(import.meta.env.VITE_RELEASE_ID) || 'unknown';
}

function sanitizeString(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  const masked = trimmed.replace(TOKEN_LIKE_RE, '[REDACTED]');
  if (masked.length <= MAX_STRING_LENGTH) {
    return masked;
  }
  return `${masked.slice(0, MAX_STRING_LENGTH)}...`;
}

function sanitizeProperties(properties: ProductAnalyticsProperties): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {
    deploy_sha: deploySha(),
    env: envName(),
    release_id: releaseId(),
    service: SERVICE_NAME,
    surface: 'web_admin',
  };

  for (const [key, value] of Object.entries(properties)) {
    if (!key || value === null || value === undefined) {
      continue;
    }
    if (typeof value === 'string') {
      const sanitizedValue = sanitizeString(value);
      if (sanitizedValue) {
        sanitized[key] = sanitizedValue;
      }
      continue;
    }
    if (typeof value === 'number') {
      if (Number.isFinite(value)) {
        sanitized[key] = value;
      }
      continue;
    }
    if (typeof value === 'boolean') {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

export function buildProductAnalyticsConfig(
  env: ProductAnalyticsEnv = currentEnv(),
): PartialPostHogConfig {
  return {
    api_host: env.apiHost,
    autocapture: false,
    capture_pageleave: false,
    capture_pageview: false,
    disable_session_recording: true,
    person_profiles: 'identified_only',
  };
}

async function loadAdapter(): Promise<ProductAnalyticsAdapter | null> {
  if (testAdapter) {
    return testAdapter;
  }
  if (activeAdapter) {
    return activeAdapter;
  }

  const env = currentEnv();
  if (!env.enabled) {
    return null;
  }
  if (initPromise) {
    return initPromise;
  }

  initPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(env.apiKey, buildProductAnalyticsConfig(env));
      if (env.debugEnabled && typeof posthog.debug === 'function') {
        posthog.debug(true);
      }
      activeAdapter = posthog;
      return posthog;
    })
    .catch((error) => {
      if (import.meta.env.DEV) {
        console.warn('[product-analytics] Failed to initialize PostHog', error);
      }
      return null;
    });

  return initPromise;
}

function withAdapter(run: (adapter: ProductAnalyticsAdapter) => void): void {
  if (testAdapter) {
    run(testAdapter);
    return;
  }

  if (activeAdapter) {
    run(activeAdapter);
    return;
  }

  const env = currentEnv();
  if (!env.enabled) {
    return;
  }

  void loadAdapter().then((adapter) => {
    if (adapter) {
      run(adapter);
    }
  });
}

export function initProductAnalytics(): void {
  if (initStarted) {
    return;
  }
  initStarted = true;
  void loadAdapter();
}

export function isProductAnalyticsEnabled(): boolean {
  return Boolean(testAdapter || currentEnv().enabled);
}

export function captureProductAnalytics(
  eventName: string,
  properties: ProductAnalyticsProperties = {},
): void {
  if (!eventName.trim()) {
    return;
  }

  const payload = sanitizeProperties(properties);
  withAdapter((adapter) => {
    adapter.capture(eventName, payload);
  });
}

export function identifyProductAnalyticsUser(user: User | null | undefined): void {
  if (!user) {
    withAdapter((adapter) => {
      adapter.reset();
    });
    return;
  }

  const distinctId = user.id.trim();
  if (!distinctId) {
    return;
  }

  const role = sanitizeString(user.role);
  const roleLabel = sanitizeString(user.role_label);

  withAdapter((adapter) => {
    adapter.identify(distinctId, {
      role: role || undefined,
      role_label: roleLabel || undefined,
    });
  });
}

export function resetProductAnalytics(): void {
  withAdapter((adapter) => {
    adapter.reset();
  });
}

export function __resetProductAnalyticsForTests(): void {
  activeAdapter = null;
  initPromise = null;
  initStarted = false;
  testAdapter = null;
}

export function __setProductAnalyticsAdapterForTests(adapter: ProductAnalyticsAdapter | null): void {
  testAdapter = adapter;
}
